import { Card, Divider, Popconfirm, Input, Select } from "antd";
import ActivityLoaderMintbox from "Components/ActivityLoaderMintbox";
import { CardHeader } from "Components/Atoms/CardHeader";
import React from "react";
import { AiFillEdit } from "react-icons/ai";
import { toast } from "react-toastify";
import AdminServicesPYV2 from "services/api/AdminServicesPYV2";
import EditKBArticle from "./EditKBArticle";

export default function AllKBArticles() {

    const [allArticles, setAllArticles] = React.useState([]);
    const [articlesLoading, setArticlesLoading] = React.useState(true);
    const [isEditing, setIsEditing] = React.useState(false);
    const [selectedArticle, setSelectedArticle] = React.useState({});

    // filters
    const [search, setSearch] = React.useState('');
    const [authorFilter, setAuthorFilter] = React.useState(null);
    const [typeFilter, setTypeFilter] = React.useState(null);
    const [categoryFilter, setCategoryFilter] = React.useState(null);

    const getAllArticles = async () => {
        let res = await AdminServicesPYV2.getAllKBArticlesV2();
        if (res?.data) {
            const parsed = JSON.parse(res.data);
            const clean = parsed.filter(el => el?.IsDeleted !== 1);

            // preprocess
            const enriched = clean.map(el => ({
                ...el,
                treeArray: el.Tree ? el.Tree.split('|') : [],
                treeString: el.Tree ? el.Tree.replaceAll('|', ' > ') : 'Uncategorized'
            }));

            setAllArticles(enriched);
        }
    };

    const loadInitData = async () => {
        setArticlesLoading(true);
        await getAllArticles();
        setArticlesLoading(false);
    };

    const handleDelete = async (article) => {
        try {
            const now = Date.now();
            const updated = { ...article, IsDeleted: 1, UpdatedOn: now };
            delete updated?._id;

            let res = await AdminServicesPYV2.upsertKBArticle({
                data: { params: JSON.stringify(updated) },
                article_id: article?._id
            });

            if (res?.type) toast.info('Article deleted!');
            await getAllArticles();
        } catch {
            toast.error("Failed to delete article");
        }
    };

    React.useEffect(() => {
        loadInitData();
    }, []);

    // ---------- FILTERING ----------
    const filteredArticles = React.useMemo(() => {
        let temp = [...allArticles];

        if (search) {
            const s = search.toLowerCase();
            temp = temp.filter(el =>
                el.Title?.toLowerCase().includes(s) ||
                el.Description?.toLowerCase().includes(s) ||
                el.Slug?.toLowerCase().includes(s)
            );
        }

        if (authorFilter) {
            temp = temp.filter(el => el.Author === authorFilter);
        }

        if (typeFilter) {
            temp = temp.filter(el => el.Type === typeFilter);
        }

        if (categoryFilter) {
            temp = temp.filter(el => el.treeArray?.includes(categoryFilter));
        }

        return temp;
    }, [allArticles, search, authorFilter, typeFilter, categoryFilter]);

    // ---------- OPTIONS ----------
    const authors = [...new Set(allArticles.map(a => a.Author).filter(Boolean))];
    const categories = [...new Set(allArticles.flatMap(a => a.treeArray))];

    return (
        <React.Fragment>
            {!isEditing ? <>
                <Card style={{ padding: 0 }} bodyStyle={{ padding: "1rem" }}>

                    <div className='d-flex justify-content-between flex-column flex-md-row' style={{ gap: "10px" }}>
                        <CardHeader heading={"All articles"} />
                    </div>

                    {/* 🔍 FILTER BAR */}
                    <div style={{ display: "flex", gap: "10px", marginTop: "1rem", flexWrap: "wrap" }}>
                        <Input
                            placeholder="Search articles..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ width: "250px" }}
                        />

                        <Select
                            placeholder="Author"
                            allowClear
                            value={authorFilter}
                            onChange={setAuthorFilter}
                            style={{ width: "180px" }}
                        >
                            {authors.map(a => <Select.Option key={a} value={a}>{a}</Select.Option>)}
                        </Select>

                        <Select
                            placeholder="Type"
                            allowClear
                            value={typeFilter}
                            onChange={setTypeFilter}
                            style={{ width: "150px" }}
                        >
                            <Select.Option value="article">Article</Select.Option>
                            <Select.Option value="dynamic">Dynamic</Select.Option>
                        </Select>

                        <Select
                            placeholder="Category"
                            allowClear
                            value={categoryFilter}
                            onChange={setCategoryFilter}
                            style={{ width: "200px" }}
                        >
                            {categories.map(c => <Select.Option key={c} value={c}>{c}</Select.Option>)}
                        </Select>
                    </div>

                    <Divider style={{ margin: "1rem 0" }} />

                    {articlesLoading ? (
                        <ActivityLoaderMintbox />
                    ) : (
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr",
                            gap: "1.25rem"
                        }}>
                            {filteredArticles.map((item, i) => (
                                <Card
                                    key={i}
                                    style={{ background: "var(--grey3)", border: "1px solid var(--grey2)" }}
                                    bodyStyle={{ padding: "1rem", height: "100%" }}
                                >
                                    <div className="d-flex flex-column justify-content-between" style={{ height: "100%", gap: "0.5rem" }}>

                                        <div>
                                            <div className='textMD w-500'>{item.Title}</div>
                                            <div className='textXS dark3 mt-1'>
                                                {item.treeString}
                                            </div>
                                            <Divider style={{ margin: "0.5rem 0" }} />
                                            <div className='textSM'>{item.Description}</div>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mt-2">
                                            <div className="textXS dark3">
                                                {item.Author || "Unknown"}
                                            </div>

                                            <div className="d-flex" style={{ gap: "0.5rem" }}>
                                                <Popconfirm
                                                    title='Delete this article?'
                                                    onConfirm={() => handleDelete(item)}
                                                >
                                                    <button className="btnRed">Delete</button>
                                                </Popconfirm>

                                                <button
                                                    className="btnPrimary d-flex align-items-center"
                                                    style={{ gap: "4px" }}
                                                    onClick={() => {
                                                        setSelectedArticle(item);
                                                        setIsEditing(true);
                                                    }}
                                                >
                                                    <AiFillEdit /> Edit
                                                </button>
                                            </div>
                                        </div>

                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </Card>
            </> : (
                <EditKBArticle
                    selectedArticle={selectedArticle}
                    allArticles={allArticles}
                    onSuccess={async () => {
                        setIsEditing(false);
                        setSelectedArticle({});
                        await getAllArticles();
                    }}
                    onCancel={() => {
                        setIsEditing(false);
                        setSelectedArticle({});
                    }}
                />
            )}
        </React.Fragment>
    );
}